import { h, Component, createRef } from "preact";
import { stopPropagation } from "../../foundation/utils/legacy";
import classname from "../../foundation/utils/classname";
import * as imageUtils from "../../foundation/utils/image";

import styles from "./HideableInfo.module.css";

interface Props {
    class?: string;
    infoText: string;
    hideButtonText?: string;
    anchor?: "top" | "bottom" | "right" | "left";
    emoji?: string;
    actionButton?: {
        text: string;
        onClick: () => void;
    };
    position?: {
        top?: number | string;
        right?: number | string;
        bottom?: number | string;
        left?: number | string;
    };
    anchorPosition?: {
        top?: number | string;
        right?: number | string;
        bottom?: number | string;
        left?: number | string;
    };
    noControls?: boolean;
    hideOnDocumentClick?: boolean;
    visible?: boolean;
    setVisible?: (visible: boolean) => void;
    onHide?: () => void;
}

const DEFAULT_ANCHOR = "bottom";
const DEFAULT_HIDE_BUTTON_TEXT = "OK, got it!";
const DEFAULT_EMOJI = "💁‍♀️";

class HideableInfo extends Component<Props> {
    override state = {
        localVisible: true
    };

    hideableInfoRef = createRef();

    get shouldHideOnDocumentClick() {
        const { noControls, hideOnDocumentClick = true } = this.props;
        return noControls && hideOnDocumentClick;
    }

    get isVisible() {
        const { visible } = this.props;
        if (visible !== null && visible !== undefined) {
            return visible;
        }

        const { localVisible } = this.state;
        return localVisible;
    }

    setVisibility = (visibility: boolean) => {
        const { visible, setVisible } = this.props;
        if (visible !== null && visible !== undefined) {
            setVisible?.(visibility);
            return;
        }

        this.setState({ localVisible: visibility });
    };

    handleClose = () => {
        const { onHide } = this.props;

        onHide?.();

        this.setVisibility(false);
    };

    handleDocumentClick = (event: MouseEvent) => {
        const isEventInHideableInfo = event.target instanceof Element && event.target.closest(`.${styles.hideableInfo}`);
        if (isEventInHideableInfo) {
            return;
        }

        this.handleClose();
    };

    override componentDidMount() {
        if (this.shouldHideOnDocumentClick) {
            document.addEventListener("click", this.handleDocumentClick, true);
        }
    }

    override componentWillUnmount() {
        if (this.shouldHideOnDocumentClick) {
            document.removeEventListener("click", this.handleDocumentClick, true);
        }
    }

    renderActionButton = () => {
        const { actionButton } = this.props;
        if (!actionButton) {
            return;
        }

        return (
            <button class={styles.infoActionButton} onClick={actionButton.onClick}>
                {actionButton.text}
            </button>
        );
    };

    renderInfoButtons = () => {
        const { noControls, hideButtonText } = this.props;
        if (noControls) {
            return;
        }

        return (
            <div class={styles.infoButtons}>
                <button class={styles.infoHideButton} onClick={this.handleClose}>
                    <div>
                        <img src={imageUtils.imageUrl("img/icGuidelineRemove.svg")} alt="&times;"/>
                        <span>
                            {hideButtonText ?? DEFAULT_HIDE_BUTTON_TEXT}
                        </span>
                    </div>
                </button>
                {this.renderActionButton()}
            </div>
        );
    };

    render() {
        if (!this.isVisible) {
            return null;
        }

        const {
            class: cssClass,
            infoText,
            emoji,
            anchor,
            noControls,
            position,
            anchorPosition
        } = this.props;

        const classnames = classname(styles.hideableInfo, cssClass, {
            [styles.noControls]: noControls
        });

        const anchorPositionClassName = styles[anchor ?? DEFAULT_ANCHOR];

        return (
            <div
                class={classnames}
                style={{ ...position }}
                ref={this.hideableInfoRef}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}>
                <span class={styles.emoji}>{emoji ?? DEFAULT_EMOJI}</span>
                <div class={styles.infoBody}>
                    <div class={styles.infoText}>{infoText}</div>
                    {this.renderInfoButtons()}
                </div>
                <div class={classname(styles.anchor, anchorPositionClassName)} style={{ ...anchorPosition }}/>
            </div>
        );
    }
}

export default HideableInfo;
export { Props as HideableInfoProps };
